import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MacrosSummary from "@/components/MacrosSummary";
import { Clock, ArrowUpRight, Ruler, Image, StarFour, Ticket, Warning } from "@phosphor-icons/react";
import { formatItalianDate } from "@/lib/dateFormat";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [studio, setStudio] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: p }, { data: m }, { data: ph }] = await Promise.all([
        api.get(`/clients/${user.user_id}/plans`),
        api.get(`/clients/${user.user_id}/measurements`),
        api.get(`/clients/${user.user_id}/photos`),
      ]);
      setPlans(p); setMeasurements(m); setPhotos(ph);
    })();
    api.get("/studio-settings").then(({ data }) => setStudio(data)).catch(() => {});
  }, [user]);

  const activePlans = useMemo(() => plans.filter(p => p.active), [plans]);
  const latest = measurements[measurements.length - 1];
  const first = measurements[0];

  return (
    <div data-testid="client-dashboard">
      <div className="label-tiny">Benvenuto/a</div>
      <h1 className="font-serif text-5xl lg:text-6xl tracking-tight mt-2">Ciao, {user?.name?.split(" ")[0]}.</h1>
      <p className="mt-3 text-sm text-[color:var(--text-secondary)] max-w-lg">
        Il tuo percorso, in un colpo d'occhio. Piani attivi, ultimi rilievi e foto.
      </p>

      <div className="mt-10 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8">
        <div className="space-y-6">
          {activePlans.length === 0 && (
            <div className="border border-dashed border-[color:var(--border)] rounded-2xl p-10 text-center text-sm text-[color:var(--text-secondary)]">
              Nessun piano attivo. Il tuo coach sta preparando il tuo primo piano.
            </div>
          )}

          {activePlans.length === 1 && (
            <div className="border-2 border-[color:var(--text-primary)] rounded-2xl p-6 bg-[color:var(--bg-primary)]" data-testid={`active-plan-${activePlans[0].plan_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="label-tiny inline-flex items-center gap-1">
                    <StarFour size={12} weight="fill" style={{ color: "var(--accent-2)" }} /> Piano attivo
                  </div>
                  <div className="font-serif text-3xl mt-1">{activePlans[0].name}</div>
                </div>
                <Link to={`/piani/${activePlans[0].plan_id}`} className="text-sm inline-flex items-center gap-1 hover:text-[color:var(--accent)] font-medium">
                  Apri dettaglio <ArrowUpRight size={14} weight="bold" />
                </Link>
              </div>
              <div className="mt-4 space-y-3">
                {(activePlans[0].meals || []).map((m) => (
                  <div key={m.id} className="flex items-center justify-between text-sm border-t border-[color:var(--border)] pt-3">
                    <div className="flex items-center gap-3">
                      <Clock size={14} weight="duotone" className="text-[color:var(--text-secondary)]" />
                      <div>
                        <div>{m.name} <span className="text-xs text-[color:var(--text-secondary)] ml-1">{m.time}</span></div>
                        <div className="text-xs text-[color:var(--text-secondary)]">
                          {Object.entries(m.portions || {}).filter(([_,v]) => v > 0).map(([k, v]) => `${v}× ${k}`).join(" · ") || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activePlans.length > 1 && (
            <div className="space-y-3" data-testid="active-plans-list">
              <div className="label-tiny inline-flex items-center gap-1">
                <StarFour size={12} weight="fill" style={{ color: "var(--accent-2)" }} /> {activePlans.length} piani attivi — scegli quale aprire
              </div>
              {activePlans.map((p) => (
                <Link
                  key={p.plan_id}
                  to={`/piani/${p.plan_id}`}
                  data-testid={`active-plan-${p.plan_id}`}
                  className="block border-2 border-[color:var(--text-primary)] rounded-2xl p-5 bg-[color:var(--bg-primary)] hover:-translate-y-0.5 transition-transform"
                  style={{ transition: "transform 0.2s ease" }}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-serif text-2xl leading-tight">{p.name}</div>
                      <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                        {p.totals?.calories || 0} kcal · {(p.meals?.length || 0)} pasti
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1 text-sm font-medium">
                      Apri <ArrowUpRight size={14} weight="bold" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/rilievi" className="border border-[color:var(--border)] rounded-2xl p-6 bg-[color:var(--bg-primary)] hover:-translate-y-1 transition-transform block" style={{ transition: "transform 0.2s ease" }}>
              <div className="label-tiny flex items-center gap-1"><Ruler size={12} weight="duotone" /> Ultimo rilievo</div>
              {latest ? (
                <>
                  <div className="font-sans text-xl font-semibold tabular-nums mt-2 text-[color:var(--text-primary)]">{formatItalianDate(latest.date)}</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                    Peso {latest.peso_kg}kg · BF {(latest.peso_kg && (latest.bodyfat_pct*100).toFixed(1)) || 0}% · MM {latest.massa_magra_kg}kg
                  </div>
                  {first && first !== latest && (
                    <div className="mt-4 text-xs">
                      <span className="text-[color:var(--text-secondary)]">Δ peso: </span>
                      <span className="tabular-nums">{(latest.peso_kg - first.peso_kg).toFixed(1)}kg</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-[color:var(--text-secondary)] mt-2">Nessun rilievo ancora.</div>
              )}
            </Link>
            <Link to="/foto" className="border border-[color:var(--border)] rounded-2xl p-6 bg-[color:var(--bg-primary)] hover:-translate-y-1 transition-transform block" style={{ transition: "transform 0.2s ease" }}>
              <div className="label-tiny flex items-center gap-1"><Image size={12} weight="duotone" /> Foto</div>
              {photos[0]?.url ? (
                <div className="mt-3 flex items-center gap-3">
                  <img src={photos[0].url} alt="" className="w-16 h-20 object-cover rounded-lg" />
                  <div>
                    <div className="font-sans text-xl font-semibold tabular-nums text-[color:var(--text-primary)]">{formatItalianDate(photos[0].date)}</div>
                    <div className="text-xs text-[color:var(--text-secondary)] uppercase tracking-wider mt-0.5">{photos[0].label || "—"}</div>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <div className="font-serif text-2xl">Galleria</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-1">Nessuna foto ancora.</div>
                </div>
              )}
            </Link>
          </div>
        </div>

        <div className="space-y-6">
          {(() => {
            const total = Number(user?.sessions_total) || 0;
            const paid = (user?.attendance || []).filter((a) => a.paid).length;
            const remaining = total - paid;
            const exhausted = remaining <= 0 && total > 0;
            if (total <= 0 && paid === 0) return null;
            return (
              <div
                data-testid="client-sessions-card"
                className="rounded-2xl border p-5"
                style={{
                  borderColor: exhausted ? "#B94A4A" : "var(--border)",
                  background: exhausted ? "rgba(185,74,74,0.06)" : "var(--bg-primary)",
                }}
              >
                <div className="label-tiny inline-flex items-center gap-1">
                  <Ticket size={12} weight="duotone" style={{ color: exhausted ? "#B94A4A" : "var(--accent)" }} />
                  Pacchetto sedute
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span
                    data-testid="client-sessions-remaining"
                    className="font-serif text-4xl tabular-nums"
                    style={{ color: remaining < 0 ? "#B94A4A" : "var(--text-primary)" }}
                  >
                    {remaining}
                  </span>
                  <span className="text-xs text-[color:var(--text-secondary)]">
                    {remaining === 1 ? "seduta rimanente" : "sedute rimanenti"}
                  </span>
                </div>
                <div className="text-[11px] text-[color:var(--text-secondary)] mt-1 tabular-nums">
                  {paid} usate · {total} totali
                </div>
                {exhausted && (
                  <div className="mt-3 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: "#B94A4A", color: "#fff" }}>
                    <Warning size={12} weight="fill" /> Pacchetto esaurito
                  </div>
                )}
              </div>
            );
          })()}
          {activePlans.length === 0 ? null : (
            <div className="space-y-6" data-testid="active-plans-macros">
              {activePlans.map((p) => (
                <div key={p.plan_id} data-testid={`plan-macros-${p.plan_id}`} className="space-y-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <StarFour size={11} weight="fill" style={{ color: "var(--accent-2)" }} className="flex-shrink-0" />
                    <div className="label-tiny truncate">{p.name}</div>
                  </div>
                  <MacrosSummary totals={p.totals || {}} />
                </div>
              ))}
            </div>
          )}
          {/* Static porzionario intentionally removed from client dashboard — clients now use /porzionario */}
        </div>
      </div>
    </div>
  );
}
